/**
 * product-form.js -- PDP add-to-cart orchestrator.
 *
 * Mounts on every `[data-product-form]` form in the document and:
 *   1. Mirrors the selected size radio's `value` into the hidden `id`
 *      input on `change` so the form always submits the correct variant.
 *      Updates the submit button label and the displayed price/compare-at
 *      text by reading the `data-variants` JSON blob on the form.
 *   2. Intercepts submit and POSTs to `/cart/add.js`. On success, reads
 *      the full cart from `/cart.js` and dispatches a `cart:updated`
 *      `CustomEvent` on `document` with that cart as `event.detail`, so
 *      the cart drawer (Epic 6 / #41) can open in place and the header
 *      badge can read `item_count`. On failure, renders the Shopify error
 *      message inline above the button.
 *   3. Validates the size selection client-side. If no size is chosen,
 *      shows the inline `select_size_validation` copy and aborts the
 *      POST.
 *
 * No framework. Vanilla JS, no dependencies, defer-loaded.
 */

(function () {
  'use strict';

  // #245: the active customer currency, read from the form's data-currency-code
  // (cart.currency.iso_code) at mount. Falls back to AUD only if the attribute
  // is absent. Previously hardcoded "AUD", which showed the wrong code on the
  // US market when a variant changed. Mirrors product-card.liquid's #1p fix.
  var currencyCode = 'AUD';

  function parseVariants(form) {
    const raw = form.getAttribute('data-variants');
    if (!raw) return [];
    try {
      return JSON.parse(raw);
    } catch (err) {
      console.warn('[product-form] invalid data-variants JSON', err);
      return [];
    }
  }

  function findVariantById(variants, id) {
    const numericId = Number(id);
    return variants.find((variant) => variant.id === numericId) || null;
  }

  function updateOverviewPrice(variant, overview) {
    if (!variant || !overview) return;
    const priceEl = overview.querySelector('.product-overview__price-current');
    const compareEl = overview.querySelector('.product-overview__price-compare');
    if (priceEl) {
      priceEl.textContent = formatMoney(variant.price);
      priceEl.classList.toggle(
        'product-overview__price-current--sale',
        variant.compare_at_price && variant.compare_at_price > variant.price,
      );
    }
    if (compareEl) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        compareEl.textContent = formatMoney(variant.compare_at_price);
        compareEl.removeAttribute('hidden');
      } else {
        compareEl.setAttribute('hidden', '');
      }
    }
  }

  function formatMoney(cents) {
    // Match the theme convention `money_with_currency | replace: '.00', ''`:
    // keep 2 decimals only for non-zero cents, strip the trailing .00 for
    // whole-dollar amounts (e.g. 9900 -> "$99 AUD", 9995 -> "$99.95 AUD").
    const dollars = (Number(cents) / 100).toFixed(2).replace(/\.00$/, '');
    return `$${dollars} ${currencyCode}`;
  }

  function clearError(errorEl) {
    if (!errorEl) return;
    errorEl.textContent = '';
    errorEl.setAttribute('hidden', '');
  }

  function showError(errorEl, message) {
    if (!errorEl) return;
    errorEl.textContent = message;
    errorEl.removeAttribute('hidden');

    // #340: the mobile sticky ATC submits this form while it is scrolled out
    // of view, so an error written here was invisible and the failed add
    // looked like a dead tap. Bring an off-screen error into view; the
    // in-page button never triggers this because its error sits above it.
    const rect = errorEl.getBoundingClientRect();
    if (rect.top < 0 || rect.bottom > window.innerHeight) {
      errorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  function getSelectedSize(form) {
    /* Support both <select> (new) and <input type="radio"> (legacy) */
    const select = form.querySelector('select[name="options[Size]"]');
    if (select) return select.value ? select : null;
    return form.querySelector('input[name="options[Size]"]:checked');
  }

  function hasSizeSelector(form) {
    return Boolean(
      form.querySelector('select[name="options[Size]"]') ||
      form.querySelector('input[name="options[Size]"]')
    );
  }

  function toggleAvailability(form, variant) {
    const submitBtn = form.querySelector('[data-product-form-submit]');
    const oosRoot = document.querySelector('[data-product-oos-form]');
    const isAvailable = Boolean(variant && variant.available);
    if (submitBtn) {
      if (isAvailable) submitBtn.removeAttribute('hidden');
      else submitBtn.setAttribute('hidden', '');
    }
    if (oosRoot) {
      if (isAvailable) {
        oosRoot.setAttribute('hidden', '');
      } else {
        oosRoot.removeAttribute('hidden');
        if (variant && variant.id) oosRoot.dataset.variantId = variant.id;
      }
    }
  }

  function onSizeChange(form, variants) {
    const idInput = form.querySelector('[data-product-form-variant-id]');
    const errorEl = form.querySelector('[data-product-form-error]');
    const overview = document.querySelector('.product-overview');
    const placeholder = form.querySelector('[data-product-size-placeholder]');

    return function handler(event) {
      const target = event.target;
      if (!target || target.name !== 'options[Size]') return;
      // The size selector exposes sold-out variants as disabled radios, but we
      // still want a click on a sold-out option to surface the OOS waitlist --
      // so we always sync the variant id, then let the availability toggle
      // route the user to the right CTA (add-to-cart vs waitlist).
      const variantId = target.value;
      if (idInput) idInput.value = variantId;
      clearError(errorEl);
      if (placeholder) placeholder.setAttribute('hidden', '');
      const variant = findVariantById(variants, variantId);
      updateOverviewPrice(variant, overview);
      toggleAvailability(form, variant);
    };
  }

  function onSubmit(form, variants) {
    const errorEl = form.querySelector('[data-product-form-error]');
    const submitButton = form.querySelector('[data-product-form-submit]');
    // M4h belt-and-braces: in the Chrome mobile emulator a touch+click
    // pair can fire two submit events spaced just past the in-flight
    // window of the `dataset.submitting` sentinel, so the second event
    // arrives after the fetch has resolved and `submitting` has been
    // cleared. Track the last successful-submit timestamp and reject
    // any submit within 300ms of it. Doesn't affect real-device taps
    // (intentional re-tap is always >300ms apart in normal use).
    let lastSubmitAt = 0;

    return async function handler(event) {
      // #252 / QA Slide 19: ALWAYS take over submission first so the native
      // `<form action="/cart/add">` can never POST + 302 to /cart. The size
      // control was migrated from `<select required>` to radios with no
      // `required`, so native HTML validation no longer blocks a no-size
      // submit - previously the browser submitted the default variant and
      // redirected to the cart page (adding the wrong item and leaving the
      // drawer flow). preventDefault must run before any early return.
      event.preventDefault();

      // Size required: surface the inline validation message and focus the
      // size control instead of adding the default variant. (JS now owns this
      // validation; the native `required` assumption no longer holds.)
      if (hasSizeSelector(form) && !getSelectedSize(form)) {
        const sizeMessage = form.getAttribute('data-select-size-message');
        showError(errorEl, sizeMessage || 'Please select a size.');
        const sizeControl = form.querySelector(
          'select[name="options[Size]"], input[name="options[Size]"]'
        );
        if (sizeControl && typeof sizeControl.focus === 'function') {
          sizeControl.focus();
        }
        return;
      }
      clearError(errorEl);

      // Availability gate (defence in depth): the server gate + CSS
      // already hide the add-to-cart button for sold-out variants, but
      // guard the POST too in case a stale/forced submit reaches here.
      // Resolve the same variant id the form submits (the hidden
      // `[data-product-form-variant-id]` input that onSizeChange syncs)
      // and, if it's unfound or unavailable, route the user to the
      // waitlist via the existing toggle instead of POSTing.
      const idInput = form.querySelector('[data-product-form-variant-id]');
      const selectedVariantId = idInput ? idInput.value : null;
      const selectedVariant = findVariantById(variants, selectedVariantId);
      if (!selectedVariant || selectedVariant.available === false) {
        toggleAvailability(form, selectedVariant);
        return;
      }

      // M4h: guard against double-submission. Mobile devices can fire
      // the submit handler twice from a single touch + click pair when
      // the button is double-tapped or when a touch is interpreted as
      // both events. Disable the button before the fetch starts so a
      // second submission can't enter this handler while the first is
      // in flight.
      if (submitButton && submitButton.dataset.submitting === '1') {
        return;
      }
      const now = Date.now();
      if (now - lastSubmitAt < 300) {
        return;
      }
      lastSubmitAt = now;

      let labelEl = null;
      let restoreLabel = null;
      if (submitButton) {
        submitButton.dataset.submitting = '1';
        submitButton.setAttribute('aria-busy', 'true');
        submitButton.disabled = true;
        labelEl = submitButton.querySelector('[data-product-form-submit-label]');
        const busyText = submitButton.getAttribute('data-label-busy');
        if (labelEl && busyText) {
          restoreLabel = labelEl.textContent;
          labelEl.textContent = busyText;
        }
      }

      try {
        const formData = new FormData(form);
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData,
          headers: { Accept: 'application/json' },
        });

        const data = await response.json().catch(() => null);

        if (!response.ok) {
          const message =
            (data && (data.description || data.message)) ||
            'Sorry, this item could not be added to your cart.';
          showError(errorEl, message);
          return;
        }

        // Klaviyo "Added to Cart" (#58, EP-DEP-07). The headless delivered
        // this event through InstantJS, which is not part of the theme, so
        // fire it directly on the Klaviyo onsite queue. No-ops when Klaviyo
        // is disabled (helper not loaded). `data` is the /cart/add.js response.
        if (window.RTLKlaviyo) window.RTLKlaviyo.trackAddedToCart(data);
        // Algolia "Added to cart after search" (#341). No-ops unless the
        // product was opened from search results. Passes the quantity this
        // add requested: the response's `quantity` is the line's new total.
        if (window.RTLAlgoliaInsights) {
          window.RTLAlgoliaInsights.trackAddedToCart(data, formData.get('quantity'));
        }

        // #340: `/cart/add.js` returns only the added line item, which has
        // no `item_count`, so the header bag badge had no count to show and
        // blanked until the next page load. Publish the full cart instead --
        // the same payload product-card quick-add sends. If the cart read
        // fails, still publish the line item so the drawer opens; the header
        // then keeps its current count.
        const cart = await fetch('/cart.js', {
          headers: { Accept: 'application/json' },
        })
          .then((cartResponse) => (cartResponse.ok ? cartResponse.json() : null))
          .catch(() => null);

        document.dispatchEvent(
          new CustomEvent('cart:updated', {
            detail: cart || data,
            bubbles: true,
          }),
        );
      } catch (err) {
        console.error('[product-form] add-to-cart failed', err);
        showError(errorEl, 'Sorry, this item could not be added to your cart.');
      } finally {
        if (submitButton) {
          submitButton.removeAttribute('aria-busy');
          submitButton.disabled = false;
          delete submitButton.dataset.submitting;
          if (labelEl && restoreLabel !== null) {
            const defaultText = submitButton.getAttribute('data-label-default') || restoreLabel;
            labelEl.textContent = defaultText;
          }
        }
      }
    };
  }

  function mount(form) {
    if (form.hasAttribute('data-product-form-mounted')) return;
    form.setAttribute('data-product-form-mounted', '');

    currencyCode = form.getAttribute('data-currency-code') || currencyCode;

    const variants = parseVariants(form);
    form.addEventListener('change', onSizeChange(form, variants));
    form.addEventListener('submit', onSubmit(form, variants));
  }

  function init() {
    const forms = document.querySelectorAll('[data-product-form]');
    forms.forEach(mount);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
